/**
 * Site Configuration - Club Wild
 * Global site settings, contact info, social media links
 *
 * HAF Programme Provider - Free outdoor activities for kids in Kent
 */

/**
 * Get the site URL dynamically
 * - In production: use production domain
 * - In preview/staging: use current domain (auto-detects Cloudflare Pages preview URLs)
 */
export const getSiteUrl = (): string => {
  // Production domain (will be clubwild.co.uk in future)
  const PRODUCTION_URL = "https://clubwild.pajo.ai";

  // If we're in the browser, detect the current domain
  if (typeof window !== "undefined") {
    const currentOrigin = window.location.origin;

    // If on production domain, use it
    if (currentOrigin.includes("clubwild.co.uk") || currentOrigin.includes("clubwild.pajo.ai")) {
      return currentOrigin;
    }

    // Otherwise use current domain (Cloudflare Pages preview, localhost, etc.)
    return currentOrigin;
  }

  // Fallback for SSR/build time
  return PRODUCTION_URL;
};

// Create base config
const baseConfig = {

  businessName: "Club Wild",
  tagline: "Wild Adventures for Wild Kids - FREE Outdoor Fun in Kent",
  owner: "Club Wild Adventures Ltd",

  // HAF Programme Details
  hafProgramme: {
    name: "Holiday Activities and Food Programme",
    description: "Free activities for families receiving benefits",
    county: "Kent",
    ageRange: "5-11 years",
  },

  contact: {
    email: "info@clubwild.co.uk",
    phone: "01234 567 890", // Demo placeholder
    mobile: "07123 456 789", // Demo placeholder
    address: {
      line1: "Club Wild - Maypole Farm",
      line2: "Maypole Road",
      city: "Canterbury",
      postcode: "CT3 4LN",
      county: "Kent",
    },
  },

  social: {
    facebook: {
      url: "https://facebook.com/clubwildkent", // Placeholder
      name: "Club Wild Kent",
    },
    instagram: {
      url: "https://instagram.com/clubwild", // Placeholder
      name: "@clubwild",
    },
  },

  navigation: [
    { label: "Home", path: "/", anchor: null },
    { label: "Activities", path: "/activities", anchor: null },
    { label: "Gallery", path: "/gallery", anchor: null },
    { label: "Book Now", path: "/booking", anchor: null },
    { label: "Contact", path: "/contact", anchor: null },
  ],

  legal: {
    companyName: "Club Wild Adventures Ltd",
    registeredAddress: "Wildwood Adventure Park, Forest Lane, Canterbury, Kent CT1 2XX",
    privacyPolicy: "Club Wild will only use your information for booking activities and will never pass on your details to a third party. We comply with GDPR and handle children's data responsibly.",
  },
};

// Define type for siteConfig including the runtime siteUrl
type SiteConfig = typeof baseConfig & {
  siteUrl: string;
};

// Create siteConfig with siteUrl as a getter (evaluated at runtime, not build time)
export const siteConfig = Object.defineProperty(baseConfig, 'siteUrl', {
  get: () => getSiteUrl(),
  enumerable: true,
  configurable: false,
}) as SiteConfig;
